import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { voiceApi } from '@/api/services/voice.api';
import type { ICallAnalyticsQuery, IListCallsQuery } from '../types';
import { voiceKeys } from '../query-keys';

/** The Calls page list — team and AI-answered calls, tagged (P9-8). */
export function useCalls(params?: IListCallsQuery) {
    return useQuery({
        queryKey: voiceKeys.calls.list(params),
        queryFn: () => voiceApi.listCallHistory(params),
        placeholderData: keepPreviousData,
        staleTime: 15_000,
    });
}

/**
 * The app default is one retry. A 404 is an answer, not a blip: an inbox
 * VOICE_CALL row can name an AI-answered call that this table does not
 * hold, and the drawer hands over on the 404 — retrying only delays it.
 */
export function retryUnlessNotFound(failureCount: number, error: unknown): boolean {
    const status =
        typeof error === 'object' && error !== null && 'isAxiosError' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined;
    if (status === 404) return false;
    return failureCount < 1;
}

export function useCall(id: string | undefined) {
    return useQuery({
        queryKey: voiceKeys.calls.detail(id ?? ''),
        queryFn: () => voiceApi.getCallById(id as string),
        enabled: Boolean(id),
        retry: retryUnlessNotFound,
    });
}

export function useCallAnalytics(params?: ICallAnalyticsQuery) {
    return useQuery({
        queryKey: voiceKeys.calls.analytics(params),
        queryFn: () => voiceApi.getCallAnalytics(params),
        staleTime: 30_000,
    });
}
